import React, { useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import PropTypes from "prop-types";
import { ReactComponent as RobotFace } from "../images/robotFace.svg";

const useStyles = makeStyles((theme) => ({
	root: {
		position: "fixed",
		bottom: 22,
		right: 22,
		display: "flex",
		flexDirection: "column",
		alignItems: "flex-end",
		gap: 12,
		zIndex: 1000,
		fontFamily: "var(--font-sans)",
	},
	welcomeMessage: {
		position: "relative",
		maxWidth: 260,
		fontSize: 14,
		fontWeight: 600,
		letterSpacing: "-0.01em",
		lineHeight: 1.35,
		color: "var(--color-dark-grey)",
		padding: "12px 16px",
		backgroundColor: "#fff",
		border: "1px solid var(--surface-border)",
		borderRadius: 16,
		boxShadow: "0 12px 32px rgba(15, 23, 42, 0.14)",
		textAlign: "left",
		animation: "$slideUp 0.35s ease",
		"&::after": {
			content: '""',
			position: "absolute",
			bottom: -7,
			right: 22,
			width: 12,
			height: 12,
			background: "#fff",
			borderRight: "1px solid var(--surface-border)",
			borderBottom: "1px solid var(--surface-border)",
			transform: "rotate(45deg)",
		},
		[theme.breakpoints.down("xs")]: {
			fontSize: 13,
			maxWidth: 220,
			padding: "10px 14px",
		},
	},
	onlineDot: {
		display: "inline-block",
		width: 8,
		height: 8,
		borderRadius: "50%",
		background: "var(--bot-status-connected)",
		marginRight: 8,
		boxShadow: "0 0 0 3px rgba(22, 163, 74, 0.18)",
		verticalAlign: "middle",
	},
	fab: {
		width: 64,
		height: 64,
		border: "none",
		borderRadius: "50%",
		cursor: "pointer",
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		padding: 0,
		background: (props) => props.primaryColor || "var(--active-brand-color)",
		boxShadow:
			"0 10px 28px rgba(11, 79, 156, 0.35), 0 2px 6px rgba(15, 23, 42, 0.12)",
		transition: "transform 0.18s ease, box-shadow 0.18s ease, opacity 0.15s ease",
		outline: "none",
		"&:hover": {
			transform: "translateY(-2px) scale(1.03)",
			boxShadow:
				"0 14px 34px rgba(11, 79, 156, 0.4), 0 4px 10px rgba(15, 23, 42, 0.14)",
		},
		"&:active": {
			transform: "translateY(0) scale(0.98)",
		},
		"&:focus-visible": {
			boxShadow: "0 0 0 4px rgba(11, 79, 156, 0.25), 0 10px 28px rgba(11, 79, 156, 0.35)",
		},
	},
	fabClose: {
		background: "#fff",
		border: "1px solid var(--surface-border)",
		boxShadow: "0 10px 28px rgba(15, 23, 42, 0.16)",
		"&:hover": {
			boxShadow: "0 14px 34px rgba(15, 23, 42, 0.2)",
		},
	},
	avatarIcon: {
		width: 36,
		height: 36,
		objectFit: "cover",
		borderRadius: "50%",
		border: "2px solid rgba(255,255,255,0.85)",
		background: "#fff",
	},
	robotIcon: {
		width: 30,
		height: 24,
		color: "#fff",
	},
	closeIcon: {
		width: 22,
		height: 22,
		color: "var(--color-dark-grey)",
	},
	pulseRing: {
		position: "absolute",
		inset: -4,
		borderRadius: "50%",
		border: (props) => `2px solid ${props.primaryColor || "var(--active-brand-color)"}`,
		opacity: 0.35,
		animation: "$pulse 2.2s ease-out infinite",
		pointerEvents: "none",
	},
	fabWrap: {
		position: "relative",
		display: "inline-flex",
	},
	"@keyframes pulse": {
		"0%": { transform: "scale(1)", opacity: 0.4 },
		"70%": { transform: "scale(1.18)", opacity: 0 },
		"100%": { transform: "scale(1.18)", opacity: 0 },
	},
	"@keyframes slideUp": {
		"0%": { opacity: 0, transform: "translateY(8px)" },
		"100%": { opacity: 1, transform: "translateY(0)" },
	},
}));

function ChatWindowButton({ chatOpen, toggleChatWindow, avatarUrl, primaryColor }) {
	const classes = useStyles({ primaryColor });
	const [isAvatarError, setIsAvatarError] = useState(false);

	return (
		<div className={classes.root}>
			{!chatOpen && (
				<div className={classes.welcomeMessage} role="status">
					<span className={classes.onlineDot} aria-hidden="true" />
					We're online — how can we help?
				</div>
			)}
			<div className={classes.fabWrap}>
				{!chatOpen && <span className={classes.pulseRing} aria-hidden="true" />}
				<button
					type="button"
					className={`${classes.fab} ${chatOpen ? classes.fabClose : ""}`}
					aria-label={chatOpen ? "Close chat" : "Open chat"}
					onClick={toggleChatWindow}
				>
					{chatOpen ? (
						<svg
							className={classes.closeIcon}
							viewBox="0 0 24 24"
							fill="none"
							xmlns="http://www.w3.org/2000/svg"
							aria-hidden="true"
						>
							<path
								d="M6.5 6.5L17.5 17.5M17.5 6.5L6.5 17.5"
								stroke="currentColor"
								strokeWidth="2.2"
								strokeLinecap="round"
							/>
						</svg>
					) : avatarUrl && !isAvatarError ? (
						<img
							src={avatarUrl}
							alt=""
							className={classes.avatarIcon}
							onError={() => setIsAvatarError(true)}
						/>
					) : (
						<RobotFace className={classes.robotIcon} />
					)}
				</button>
			</div>
		</div>
	);
}

ChatWindowButton.propTypes = {
	chatOpen: PropTypes.bool.isRequired,
	toggleChatWindow: PropTypes.func.isRequired,
	avatarUrl: PropTypes.string,
	primaryColor: PropTypes.string.isRequired,
};

export default ChatWindowButton;
